
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useRememberedState } from '../../hooks/useRemembered';
import { UserProfile, ReportMetadata, ServiceItem } from '../../types';
import { Button } from '../Button';
import { Search, Archive, Plus, Loader2, FileSpreadsheet, FileJson, FileDown, Download, ArrowUp, ArrowDown, Trash2, ArrowRightLeft, FilePlus, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import { loadProjectById, saveProjectToCloud, initiateProjectTransfer, forceTransferByAdmin } from '../../services/cloudService';
import { getProjectFinancials, hasOpenBalance as projectHasOpenBalance } from '../../services/projectLv';
import { sameCompany } from '../../services/companyScope';
import { begehungsNummer, hatNochKeineBegehung } from '../../services/projectType';
import { saveProjectSupplement } from '../../services/projectSupplement';
import { ProjectSupplementModal } from '../ProjectSupplementModal';
import { AdminCsvImportModal } from './AdminCsvImportModal';
import { ProjectLockHint } from '../ProjectLockHint';
import { ArchivDokumenteDialog } from '../dokumente/ArchivDokumenteDialog';
import { DokumenteArchivEntscheidung } from '../dokumente/types';
import { beantrageProjektLoeschung, ladeOffenenAntragZumProjekt } from '../../services/projectDeletionService';
import { ladeOffeneBelegeJeProjekt, pruefeArchivierbarkeit } from '../../services/archiveGuardService';
import type { BelegZustand } from '../../services/archiveGuardLogic';
import { isAdminRole } from '../../services/projectLockLogic';
import { isProjectArchived, isProjectCompleted } from '../../services/projectBilling';
import { ProjectDeleteRequestDialog } from './ProjectDeleteRequestDialog';
import { useProjectLocks } from '../../hooks/useProjectLocks';
import { KickoffKennzeichenBadge } from '../kickoff/KickoffStatus';
import { useKickoffKennzeichen } from '../kickoff/useKickoffKennzeichen';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { ohneGesperrte } from '../../services/gesperrteKonten';
import { angezeigteAdresse, angezeigteProjektnummer, baueSuchindex, sucheImProjekt, suchbegriffe, trefferBereiche, Suchbegriffe, Suchfeld } from '../../services/projectSearch';
import { geaendertText, initialen, listenStatus, STATUS_FILTER, STATUS_LABEL } from './adminProjectListLogic';
import type { ListProject } from '../../services/listProject';
import { backupDateiname, backupFehlendText, backupFehlerText, baueBackupInhalt, ladeBackupZeilen } from '../../services/projektBackup';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Seitengroesse der Projekttabelle. 0 steht fuer "Alle" — der alte Zustand,
// in dem jede Zeile auf einmal gerendert und fuer jede Zeile die Sperre
// abgefragt wurde. Die Wahl ueberlebt den Neustart, weil sie eine
// Arbeitsgewohnheit ist und keine Filtereinstellung.
const PAGE_SIZE_STORAGE_KEY = 'osia.adminProjectList.pageSize';
const PAGE_SIZE_OPTIONS = [20, 50, 100, 0] as const;
const DEFAULT_PAGE_SIZE = 50;

const readStoredPageSize = (): number => {
    try {
        const raw = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const n = raw === null ? NaN : Number(raw);
        return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
    } catch { return DEFAULT_PAGE_SIZE; }
};

const ownerName = (owner: UserProfile | undefined): string =>
    owner ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() : 'Unbekannt';

// Farbe des Statuspunkts — die einzige Farbe der Statusspalte.
const STATUS_PUNKT: Record<string, string> = {
    planning: 'bg-blue-500',
    active: 'bg-emerald-500',
    on_hold: 'bg-amber-500',
    completed: 'bg-purple-500',
    finished: 'bg-purple-500',
};

/** Ein Text mit markierten Suchtreffern (services/projectSearch.ts). */
const Markiert: React.FC<{ text: string; begriffe: Suchbegriffe }> = ({ text, begriffe }) => {
    const bereiche = trefferBereiche(text, begriffe);
    if (bereiche.length === 0) return <>{text}</>;
    const teile: React.ReactNode[] = [];
    let stelle = 0;
    bereiche.forEach(([von, bis], i) => {
        if (von > stelle) teile.push(text.slice(stelle, von));
        teile.push(<mark key={i} className="bg-amber-100 text-inherit rounded-sm px-px">{text.slice(von, bis)}</mark>);
        stelle = bis;
    });
    if (stelle < text.length) teile.push(text.slice(stelle));
    return <>{teile}</>;
};

interface Props {
  projects: ListProject[];
  users: UserProfile[];
  currentUser: UserProfile | null;
  onOpenProject: (project: ListProject) => void;
  onCreateProject: () => void;
  onRefresh: () => void;
  onSaveStatusChange: (status: any) => void;
  customCatalog: ServiceItem[];
}

export const AdminProjectList: React.FC<Props> = ({ projects: propProjects, users, currentUser, onOpenProject, onCreateProject, onRefresh, onSaveStatusChange, customCatalog }) => {
    const [projects, setProjects] = useState<ListProject[]>(propProjects);
    
    React.useEffect(() => {
        setProjects(propProjects);
    }, [propProjects]);

    const [searchTerm, setSearchTerm] = useRememberedState('admin.projects.search', '');
    const [showOnlyNoInspections, setShowOnlyNoInspections] = useRememberedState('admin.projects.noInspections', false);
    const [sortConfig, setSortConfig] = useRememberedState<{ key: string; direction: 'asc' | 'desc' }>('admin.projects.sort', { key: 'updated_at', direction: 'desc' });
    const [pageSize, setPageSize] = useState<number>(readStoredPageSize);
    const [currentPage, setCurrentPage] = useState(1);

    const [supplementModal, setSupplementModal] = useState<{ isOpen: boolean; project: ListProject | null }>({ isOpen: false, project: null });
    const [transferModal, setTransferModal] = useState<{ isOpen: boolean; projectId: string | null; projectName: string }>({ isOpen: false, projectId: null, projectName: '' });
    const [transferTargetId, setTransferTargetId] = useState('');
    const gesperrteKonten = useGesperrteKonten();
    const [transferSearch, setTransferSearch] = useState('');
    const [deleteActionProject, setDeleteActionProject] = useState<ListProject | null>(null);
    const [isProcessingDelete, setIsProcessingDelete] = useState(false);
    // Schritt 1 des Vier-Augen-Prinzips: die Begruendung. Ein eigener Dialog,
    // weil der alte Knopf an derselben Stelle sofort geloescht hat — wer den
    // Weg im Muskelgedaechtnis hat, soll den Unterschied SEHEN.
    const [loeschAntragProjekt, setLoeschAntragProjekt] = useState<ListProject | null>(null);
    // Die Archiv-Rueckfrage nach der Dokumentenablage. Dieselbe Frage wie im
    // Projekt selbst (ProjectStats) — es gibt zwei Wege ins Archiv, und beide
    // muessen fragen, sonst bleibt die Ablage genau dann liegen, wenn ein
    // Admin aufraeumt.
    const [archivDialogProjekt, setArchivDialogProjekt] = useState<ListProject | null>(null);
    const [fixingOwnerId, setFixingOwnerId] = useState<string | null>(null);

    const [showCsvImportModal, setShowCsvImportModal] = useState(false);
    // Backup (JSON): waehrend des Ladens der Fortschritt (Menuepunkt gesperrt),
    // danach ggf. eine Meldung ueber der Tabelle — das Menue ist dann zu.
    const [backupLauf, setBackupLauf] = useState<{ geladen: number; gesamt: number } | null>(null);
    const [backupMeldung, setBackupMeldung] = useState<{ art: 'fehler' | 'hinweis'; text: string } | null>(null);

    /*
     * Die Kennzahlen kommen aus services/projectLv und werden hier NICHT mehr
     * selbst gerechnet.
     *
     * Bis hierher stand an dieser Stelle ein Doppel, und es war an zwei Stellen
     * blind: es las ausschliesslich `meta.globalData.services` (also den
     * JSON-Blob, waehrend getNormalizedServices die relationale Quelle
     * bevorzugt) und es kannte weder die Alt-Deckung noch den Verzicht. Folge:
     * ein Projekt, an dem die letzte offene Menge geschlossen wurde, galt hier
     * weiter als "Abschluss (Offen)" und blieb aus der Archivliste
     * ausgesperrt — waehrend die Buchhaltungsansicht daneben 0,00 zeigte.
     * Zwei Wahrheiten ueber dieselbe Zahl, und die falsche entschied ueber die
     * Sichtbarkeit.
     */

    // Statusschalter und Verantwortlichen-Auswahl ueber der Tabelle. 'alle'
    // heisst: kein Filter.
    const [statusFilter, setStatusFilter] = useRememberedState('admin.projects.status', 'alle');
    const [ownerFilter, setOwnerFilter] = useRememberedState('admin.projects.owner', 'alle');
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const schliessen = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
        const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('mousedown', schliessen);
        document.addEventListener('keydown', taste);
        return () => { document.removeEventListener('mousedown', schliessen); document.removeEventListener('keydown', taste); };
    }, [menuOpen]);

    // Offene Belege der Flotte, je Projekt — fuer das Archiv mit unbezahlter
    // Rechnung (siehe unten). `null` = nicht geladen oder nicht ladbar.
    // Neu geladen mit jeder neuen Projektliste (Aktualisieren): eine Zahlung
    // dazwischen soll die Zeile nicht bis zum naechsten Oeffnen stehen lassen.
    const [offeneBelege, setOffeneBelege] = useState<Map<string, BelegZustand[]> | null>(null);
    useEffect(() => {
        let cancelled = false;
        ladeOffeneBelegeJeProjekt().then(belege => {
            if (!cancelled) setOffeneBelege(belege);
        });
        return () => { cancelled = true; };
    }, [propProjects]);

    // Alles, was dieser Admin ueberhaupt sehen darf — vor Suche und Filtern.
    const scopedProjects = useMemo(() => {
        if (!currentUser) return [];
        let list = projects;
        if (currentUser.role !== 'super_admin') {
            const companyUserIds = users.filter(u => sameCompany(u, currentUser)).map(u => u.id);
            list = projects.filter(p => companyUserIds.includes(p.user_id));
        }
        return list.filter(p => {
             // Nur das ARCHIV ('finished') verschwindet aus der Liste. BEENDET
             // ('completed') bleibt sichtbar — dort steht die Schlussrechnung aus.
             // Eine eigene Archivansicht gibt es hier nicht mehr
             // (Betreiberentscheidung 06.10.2026): das Archiv liegt in der
             // Buchhaltung (AdminAccountingView, Historie).
             if (!isProjectArchived(p)) return true;
             // Mit zurueckgehaltenem Paketgeld (Bestandteile offen) oder einem
             // unbezahlten Beleg (vor Regel 3 archiviert, Fall 6271) bleibt
             // das Archiv sichtbar — dieselbe Frage wie in der Buchhaltung.
             return projectHasOpenBalance(getProjectFinancials(p)) || (offeneBelege?.get(p.id)?.length ?? 0) > 0;
        });
    }, [projects, currentUser, users, offeneBelege]);

    // Der Suchindex haengt nur an der Liste, nicht am Suchtext: gefaltet
    // wird einmal je Laden, nicht bei jedem Tastendruck.
    const suchindex = useMemo(() => {
        const usersById = new Map(users.map(u => [u.id, u]));
        return new Map(scopedProjects.map(p => [p.id, baueSuchindex(p, ownerName(usersById.get(p.user_id)))]));
    }, [scopedProjects, users]);

    const begriffe = useMemo(() => suchbegriffe(searchTerm), [searchTerm]);

    // Fundstellen je Projekt — nur fuer Treffer, die nicht in der Zeile stehen.
    const searchResult = useMemo(() => {
        const fundstellen = new Map<string, Suchfeld[]>();
        const list = scopedProjects.filter(p => {
            const index = suchindex.get(p.id);
            if (!index) return false;
            const ergebnis = sucheImProjekt(index, begriffe, searchTerm);
            if (ergebnis.treffer && ergebnis.fundstellen.length > 0) fundstellen.set(p.id, ergebnis.fundstellen);
            return ergebnis.treffer;
        });
        return { list, fundstellen };
    }, [scopedProjects, suchindex, begriffe, searchTerm]);

    // Zur Auswahl stehen nur Verantwortliche, die hier ein Projekt haben.
    const ownerOptions = useMemo(() => {
        const ids = new Set(scopedProjects.map(p => p.user_id));
        return users.filter(u => ids.has(u.id)).sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }, [scopedProjects, users]);
    // Ein gemerkter Verantwortlicher, der hier kein Projekt mehr hat, filtert nicht.
    const activeOwner = ownerOptions.some(u => u.id === ownerFilter) ? ownerFilter : 'alle';

    // „Archiv (offen)" gibt es nur als Ausnahme — der Schalter erscheint erst,
    // wenn ein solches Projekt in der Liste steht.
    const hasStatusFinished = scopedProjects.some(p => listenStatus(p) === 'finished');
    const statusOptions = ['alle', ...STATUS_FILTER.filter(s => s !== 'finished' || hasStatusFinished)];
    const activeStatus = statusOptions.includes(statusFilter) ? statusFilter : 'alle';

    const { filteredProjects, statusCounts, noInspectionCount } = useMemo(() => {
        const base = activeOwner === 'alle' ? searchResult.list : searchResult.list.filter(p => p.user_id === activeOwner);
        // Ohne Berichtsbefreite und SiFa-Betreuung (services/projectType.ts).
        const ohneBegehung = (list: typeof base) => list.filter(p => hatNochKeineBegehung(p));
        const nachStatus = (list: typeof base) => activeStatus === 'alle' ? list : list.filter(p => listenStatus(p) === activeStatus);

        // Jede Zahl an einem Schalter sagt, was ein Klick darauf zeigen wuerde.
        const fuerStatus = showOnlyNoInspections ? ohneBegehung(base) : base;
        const counts: Record<string, number> = { alle: fuerStatus.length };
        for (const p of fuerStatus) { const s = listenStatus(p); counts[s] = (counts[s] || 0) + 1; }

        return {
            filteredProjects: nachStatus(fuerStatus),
            statusCounts: counts,
            noInspectionCount: ohneBegehung(nachStatus(base)).length,
        };
    }, [searchResult, activeOwner, activeStatus, showOnlyNoInspections]);

    const sortedProjects = useMemo(() => {
        const list = [...filteredProjects];
        return list.sort((a, b) => {
            let valA: any = '';
            let valB: any = '';
            switch (sortConfig.key) {
                case 'name': valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase(); break;
                case 'owner':
                    const ownerA = users.find(u => u.id === a.user_id);
                    const ownerB = users.find(u => u.id === b.user_id);
                    valA = (ownerA?.last_name || '').toLowerCase(); valB = (ownerB?.last_name || '').toLowerCase();
                    break;
                case 'status':
                    const statusMap: Record<string, number> = { 'active': 5, 'planning': 4, 'on_hold': 3, 'completed': 2, 'finished': 1 };
                    // Derselbe Status wie in der Zeile und an den Schaltern.
                    const statA = listenStatus(a);
                    const statB = listenStatus(b);
                    valA = statusMap[statA] || 0; valB = statusMap[statB] || 0;
                    break;
                case 'updated_at':
                    valA = new Date(a.updated_at).getTime(); valB = new Date(b.updated_at).getTime();
                    break;
                default: return 0;
            }
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredProjects, sortConfig, users]);

    // Jede Aenderung an Suche, Filter, Sortierung oder Seitengroesse fuehrt
    // auf Seite 1 — die alte Seitennummer zeigt sonst auf eine Stelle einer
    // Liste, die es so nicht mehr gibt.
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showOnlyNoInspections, statusFilter, ownerFilter, sortConfig, pageSize]);

    useEffect(() => {
        try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize)); } catch { /* Komfort, kein Muss */ }
    }, [pageSize]);

    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(sortedProjects.length / pageSize)) : 1;

    // Schrumpft die Liste (Archivieren, Loeschen, Refresh), darf die aktuelle
    // Seite nicht ueber das Ende hinaus zeigen — sonst steht der Admin auf
    // einer leeren Seite und glaubt, es gaebe keine Projekte mehr.
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const safePage = Math.min(currentPage, totalPages);
    const pageStart = pageSize > 0 ? (safePage - 1) * pageSize : 0;
    const pageEnd = pageSize > 0 ? Math.min(pageStart + pageSize, sortedProjects.length) : sortedProjects.length;
    const pageProjects = useMemo(
        () => (pageSize > 0 ? sortedProjects.slice(pageStart, pageEnd) : sortedProjects),
        [sortedProjects, pageSize, pageStart, pageEnd]
    );
    const showPagination = pageSize > 0 && totalPages > 1;

    // Ein gebuendelter Sperr-Abruf fuer die sichtbare Tabelle — also nur die
    // aktuelle Seite, nicht die ganze gefilterte Liste. Der Hinweis ist
    // reine Vorwarnung — geoeffnet wird ein gesperrtes Projekt weiterhin.
    const { locks: projectLocks, nowMs: lockNowMs } = useProjectLocks(pageProjects.map((p) => p.id));

    // Kalender-Quittung (Projekt-Kickoff, docs/projekt-kickoff-plan.md §4.4):
    // ebenfalls EIN Abruf für die sichtbare Seite, neu nur bei geänderter
    // Projektmenge. Ohne Daten (Migration fehlt, Altprojekt) kein Kennzeichen.
    const kickoffKennzeichen = useKickoffKennzeichen(pageProjects.map(p => p.id));

    const handleSort = (key: string) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const RenderSortHeader = ({ label, sortKey }: { label: string, sortKey: string }) => {
        const isActive = sortConfig.key === sortKey;
        return (
            <th
                className="px-4 py-2.5 font-semibold"
                aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
                <button type="button" onClick={() => handleSort(sortKey)} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 select-none">
                    {label}
                    {isActive && (sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-slate-700" /> : <ArrowDown className="w-3.5 h-3.5 text-slate-700" />)}
                </button>
            </th>
        );
    };

    const handleSaveSupplement = async (updatedProjectObject: any, changeLog: string, syncCounters?: { current: number, exported: number }) => {
        if (!supplementModal.project) return;
        onSaveStatusChange('saving');
        try {
            // Gemeinsamer Speicherpfad beider Einhängepunkte des Dialogs —
            // siehe services/projectSupplement.ts.
            await saveProjectSupplement(
                supplementModal.project.id,
                updatedProjectObject,
                changeLog,
                currentUser,
                syncCounters
            );

            onRefresh();
            onSaveStatusChange('saved');
        } catch (e) { 
            console.error(e);
            alert("Fehler beim Speichern des Nachtrags");
            onSaveStatusChange('error');
        }
    };

    /**
     * Der zweite Weg ins Archiv (der erste ist der Statusumschalter im
     * Projekt, ProjectStats). Auch er fragt zuerst nach der
     * Dokumentenablage — sonst bliebe sie genau dann liegen, wenn ein Admin
     * aufraeumt.
     *
     * ARCHIV-RIEGEL: davor steht die Pruefung auf eigene offene Posten UND
     * auf offene Toechter, und zwar VOR der Dokumenten-Rueckfrage. An dem Dialog haengt das LOESCHEN von
     * Dateien; ein Riegel dahinter kaeme zu spaet. Beide Wege ins Archiv
     * muessen ihn haben — ein ungesperrter zweiter Weg macht den ersten
     * wertlos. Die Regel steht in services/archiveGuardLogic.ts.
     */
    const startArchive = async () => {
        const projekt = deleteActionProject;
        if (!projekt) return;
        // Archiv ist Verwaltungssache (Betreiberentscheidung 15.09.2026): nur
        // Administratoren, und nur aus BEENDET heraus — erst beenden, dann
        // Schlussrechnung stellen und bezahlen lassen, dann archivieren.
        if (!isAdminRole(currentUser?.role)) {
            alert('Archivieren können nur Administratoren.');
            return;
        }
        if (!isProjectCompleted(projekt)) {
            alert('Ins Archiv geht ein Projekt nur aus „Beendet" heraus. Bitte das Projekt zuerst auf „Beendet" setzen (im Projekt unter Projektstatus).');
            return;
        }
        setIsProcessingDelete(true);
        let urteil;
        try {
            urteil = await pruefeArchivierbarkeit(projekt.id);
        } finally {
            setIsProcessingDelete(false);
        }
        if (!urteil.erlaubt) {
            alert(urteil.meldung);
            return;
        }
        setArchivDialogProjekt(projekt);
        setDeleteActionProject(null);
    };

    const executeArchive = async (projekt: ListProject, dokumenteEntscheidung: DokumenteArchivEntscheidung | null) => { 
        if (!projekt) return; 
        setIsProcessingDelete(true); 
        try { 
            const fullProject = await loadProjectById(projekt.id); 
            if (fullProject) { 
                const meta = fullProject.data.metadata; 
                const globalData: any = { ...meta.globalData, status: 'finished' };
                if (dokumenteEntscheidung) globalData.documentsArchiveDecision = dokumenteEntscheidung;
                const newMeta = { ...meta, globalData }; 
                await saveProjectToCloud({ ...fullProject.data, metadata: newMeta }, fullProject.id); 
                onRefresh(); 
                alert("Projekt wurde erfolgreich ins Archiv verschoben."); 
            } 
        } catch { alert("Fehler beim Archivieren."); }
        finally { setIsProcessingDelete(false); setArchivDialogProjekt(null); } 
    };

    /**
     * Schritt 1 des Vier-Augen-Prinzips: Loeschung BEANTRAGEN, nicht loeschen.
     *
     * Frueher stand hier `deleteProjectFromCloud` und ein Projekt war nach
     * einem Klick weg. Seit supabase/project_deletion_approvals.sql
     * (MIGRATIONS.md Nr. 49) gibt es diesen Weg nicht mehr — die Datenbank
     * weist ein direktes DELETE ab. Der Antrag geht an einen zufaellig
     * ausgelosten zweiten Administrator; erst dessen Freigabe schaltet in
     * "Loeschantraege" den Knopf frei, der wirklich loescht.
     *
     * Die Dokumentenablage wird bewusst NICHT hier geraeumt, sondern erst beim
     * Ausfuehren: solange die Freigabe fehlt, darf am Projekt nichts fehlen.
     */
    const beantrageLoeschung = async (begruendung: string) => {
        const projekt = loeschAntragProjekt;
        if (!projekt) return;

        await beantrageProjektLoeschung(projekt.id, begruendung);

        // Der Pruefer steht erst in der Zeile, die die Datenbank angelegt hat —
        // ausgelost wird dort, nicht hier. Ihn zu nennen ist der halbe Zweck
        // der Rueckmeldung; klemmt das Nachladen, bleibt die Meldung eben
        // allgemein statt falsch.
        let pruefer = '';
        try {
            const antrag = await ladeOffenenAntragZumProjekt(projekt.id);
            if (antrag?.approver_name) pruefer = ` ${antrag.approver_name} wurde als Prüfer ausgelost und benachrichtigt.`;
        } catch { /* Anzeige-Beiwerk, kein Grund zur Fehlermeldung */ }

        setLoeschAntragProjekt(null);
        setDeleteActionProject(null);
        alert(`Löschantrag für "${projekt.name}" gestellt.${pruefer}

Das Projekt bleibt bestehen, bis ein zweiter Administrator zustimmt. Den Stand zeigt die Verwaltung unter "Löschanträge".`);
    };

    const confirmTransfer = async () => { 
        if (!transferModal.projectId || !transferTargetId) return; 
        try { 
            await initiateProjectTransfer(transferModal.projectId, transferTargetId); 
            setTransferModal({ isOpen: false, projectId: null, projectName: '' }); 
            setTransferTargetId(''); 
            setTransferSearch(''); 
            onRefresh(); 
            alert("Transfer-Anfrage gesendet."); 
        } catch (e: any) { alert(e.message); } 
    };
    
    const handleQuickFixOwner = async (projectId: string, correctUserId: string) => {
        if (!confirm("Soll der Datenbank-Besitzer auf den im Bericht genannten Nutzer gesetzt werden? Dies korrigiert den Besitz-Mismatch sofort.")) return;
        
        setFixingOwnerId(projectId);
        try {
            // Identität mitgeben: die Projektnotiz soll den handelnden Admin
            // nennen, nicht 'System-Admin'.
            await forceTransferByAdmin(projectId, correctUserId, currentUser);
            onRefresh();
            alert("Besitzer erfolgreich synchronisiert.");
        } catch (e: any) {
            console.error("Owner Fix Error:", e);
            alert("Korrektur fehlgeschlagen: " + (e.message || "Datenbank-Sperre"));
        } finally {
            setFixingOwnerId(null);
        }
    };

    // Backup und PDF nehmen bewusst die GANZE gefilterte Liste (sortedProjects),
    // nicht die angezeigte Seite — ein Backup, das nur 50 Zeilen kennt, waere
    // keins.
    //
    // Das Backup schreibt NICHT mehr die Listeneintraege weg: die tragen von
    // `data` nur `metadata` (listProject.ts), Unterlage, Vorankuendigung,
    // Einstellungen usw. fehlten. Es laedt die vollen Zeilen aus `projects`
    // (alle Spalten, volles `data`, `project_services` eingebettet) — rein
    // lesend, ohne Anker. Maengel mit Fotos, Checklisten, Notizen, Plaene,
    // Anhangbilder und RAB-Eintraege liegen in Kindtabellen und sind NICHT
    // enthalten; die genaue Aufstellung steht in services/projektBackup.ts.
    const backupLaeuft = useRef(false);
    const handleJsonExport = async () => {
        if (backupLaeuft.current) return;
        backupLaeuft.current = true;
        // Die Auswahl zum Zeitpunkt des Klicks — filtert der Admin waehrend
        // des Ladens weiter, aendert das die Datei nicht mehr.
        const auswahl = sortedProjects;
        setBackupMeldung(null);
        setBackupLauf({ geladen: 0, gesamt: auswahl.length });
        try {
            const { zeilen, fehlend } = await ladeBackupZeilen(auswahl.map(p => p.id), {
                onFortschritt: (geladen, gesamt) => setBackupLauf({ geladen, gesamt }),
            });
            const jetzt = new Date();
            const exportData = baueBackupInhalt(zeilen, { exportDate: jetzt.toISOString(), exportedBy: currentUser?.email });
            const fileName = backupDateiname(jetzt);
            const json = JSON.stringify(exportData, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const href = URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = href; link.download = fileName;
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            if (fehlend.length > 0) {
                const namen = fehlend.map(id => auswahl.find(p => p.id === id)?.name || id);
                setBackupMeldung({ art: 'hinweis', text: backupFehlendText(namen) });
            }
        } catch (e) {
            console.error('Backup (JSON) fehlgeschlagen:', e);
            setBackupMeldung({ art: 'fehler', text: backupFehlerText(e) });
        } finally {
            backupLaeuft.current = false;
            setBackupLauf(null);
        }
    };

    const handlePdfExport = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
            
            // Layout Header
            doc.setFillColor(33, 64, 95); // Deep corporate blue
            doc.rect(0, 0, 297, 28, 'F');
            
            // Header Text
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(255, 255, 255);
            const titleText = showOnlyNoInspections 
                ? "OFFENE POSITIONEN (PROJEKTE OHNE BEGEHUNGEN - V0)" 
                : "PROJEKTLISTE (ADMINISTRATIVE ÜBERSICHT)";
            doc.text(titleText, 15, 18);
            
            // Metadata info
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(240, 240, 240);
            const dateStr = new Date().toLocaleDateString('de-DE', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit' 
            });
            doc.text(`Generiert am: ${dateStr}  |  Anzahl Projekte: ${sortedProjects.length}  |  Ersteller: ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`, 15, 24);
            
            // Define autoTable data — die ganze gefilterte Liste, nicht die Seite
            const tableHead = [['Projekt-Nr.', 'Projektname', 'Ort / Adresse', 'Verantwortlicher (DB)', 'Status', 'Letzte Änderung']];
            const tableBody = sortedProjects.map(p => {
                const meta = p.data.metadata as ReportMetadata;
                const owner = users.find(u => u.id === p.user_id);
                const ownerName = owner ? `${owner.first_name} ${owner.last_name}` : 'Unbekannt';
                const ownerCompany = owner?.company_name ? ` (${owner.company_name})` : '';
                
                const currentStatus = meta.globalData?.status || p.status || 'planning';
                const statusLabels: Record<string, string> = { planning: "Planung", active: "Ausführung", on_hold: "Ruht", completed: "Beendet", finished: "Archiv" };
                const displayStatus = statusLabels[currentStatus] || currentStatus;
                
                const projNum = p.project_number || meta.projectNumber || 'Keine';
                const projAddress = meta.projectAddress || 'Keine Adresse';
                const lastChange = new Date(p.updated_at).toLocaleDateString('de-DE');
                
                const inspectionNum = begehungsNummer(p);

                const vStatus = inspectionNum === 0 ? "V0 (Offen)" : `V${inspectionNum}`;
                
                return [
                    projNum,
                    p.name || '',
                    projAddress,
                    `${ownerName}${ownerCompany}`,
                    `${displayStatus.toUpperCase()} (${vStatus})`,
                    lastChange
                ];
            });
            
            autoTable(doc, {
                startY: 35,
                head: tableHead,
                body: tableBody,
                theme: 'striped',
                headStyles: { fillColor: [33, 64, 95], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold' },
                styles: { fontSize: 9, cellPadding: 3, font: 'helvetica' },
                columnStyles: {
                    0: { cellWidth: 30 },
                    1: { cellWidth: 60, fontStyle: 'bold' },
                    2: { cellWidth: 65 },
                    3: { cellWidth: 65 },
                    4: { cellWidth: 35 },
                    5: { cellWidth: 22 }
                },
                alternateRowStyles: { fillColor: [248, 250, 252] }
            });
            
            const fileSuffix = showOnlyNoInspections ? "_Offene_Positionen_V0" : "_Projektliste";
            const fileName = `Sigeko_Smart_Report${fileSuffix}_${new Date().toISOString().slice(0,10)}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error("Fehler beim Generieren des PDFs:", error);
            alert("Fehler beim Erstellen des PDF-Berichts. Bitte versuchen Sie es erneut.");
        }
    };

    const handleDownloadCsvTemplate = () => {
        const headers = "Projektnummer;Projektname;Adresse;Bauherr;Startdatum;Koordinator";
        const row = "5000;Wohnpark Süd;Musterstraße 1;Musterbau AG;01.01.2024;Max Mustermann";
        const csvContent = `data:text/csv;charset=utf-8,${headers}\n${row}`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a"); link.setAttribute("href", encodedUri); link.setAttribute("download", "projekt_import_vorlage.csv");
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // Gesperrte Mitglieder (Nr. 148) bekommen keine Projekte mehr übertragen.
    const transferUsers = ohneGesperrte(users, gesperrteKonten, transferTargetId).filter(u => { if (currentUser?.role !== 'super_admin') { if (!sameCompany(u, currentUser)) return false; } const search = transferSearch.toLowerCase(); if (!search) return true; return ( (u.first_name || '').toLowerCase().includes(search) || (u.last_name || '').toLowerCase().includes(search) || (u.email || '').toLowerCase().includes(search) || (u.company_name || '').toLowerCase().includes(search) ); }).sort((a,b) => (a.last_name || '').localeCompare(b.last_name || ''));

    return (
        <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-4 pt-4 pb-3 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[15rem]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                        type="text"
                        autoComplete="off"
                        aria-label="Projekte suchen"
                        placeholder="Projekt, Nr., Straße, Ort, Bauherr, Bauleiter, Firma …"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-10 pl-10 pr-9 rounded-lg bg-slate-100 border border-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-300 focus:ring-4 focus:ring-blue-100 outline-none"
                    />
                    {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} aria-label="Suche leeren" title="Suche leeren" className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2 ml-auto">
                    {/* Backup, PDF und CSV sind Verwaltungswerkzeuge, keine
                        Tagesarbeit — ein Menue statt fuenf Knoepfen. */}
                    <div className="relative" ref={menuRef}>
                        <button type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-haspopup="menu" className="h-10 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1.5 whitespace-nowrap">
                            <Download className="w-4 h-4" />Export &amp; Import<ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {menuOpen && (
                            <div role="menu" className="absolute right-0 mt-1 w-72 bg-white rounded-xl shadow-lg py-1 z-30">
                                {[
                                    {
                                        icon: FileJson,
                                        label: 'Backup (JSON)',
                                        sub: backupLauf
                                            ? `Wird erstellt … ${backupLauf.geladen} von ${backupLauf.gesamt}`
                                            : 'Alle Projekte der aktuellen Auswahl',
                                        run: () => { void handleJsonExport(); },
                                        disabled: backupLauf !== null,
                                    },
                                    { icon: Download, label: 'Liste als PDF', sub: 'Aktuelle Auswahl, wie gefiltert', run: handlePdfExport },
                                    null,
                                    { icon: FileSpreadsheet, label: 'CSV importieren', sub: 'Projekte aus einer Tabelle anlegen', run: () => setShowCsvImportModal(true) },
                                    { icon: FileDown, label: 'CSV-Vorlage herunterladen', sub: 'Spalten für den Import', run: handleDownloadCsvTemplate },
                                ].map((eintrag, i) => eintrag === null
                                    ? <div key={i} className="my-1 h-px bg-slate-100" />
                                    : (
                                        <button key={i} type="button" role="menuitem" disabled={!!eintrag.disabled} aria-disabled={!!eintrag.disabled} onClick={() => { setMenuOpen(false); eintrag.run(); }} className="w-full text-left px-3 py-2 flex items-start gap-3 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                            <eintrag.icon className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                                            <span>
                                                <span className="block text-sm font-semibold text-slate-700">{eintrag.label}</span>
                                                <span className="block text-xs text-slate-500">{eintrag.sub}</span>
                                            </span>
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                    <Button variant="primary" onClick={onCreateProject} icon={<Plus className="w-4 h-4"/>} className="h-10 whitespace-nowrap">Neues Projekt</Button>
                </div>
            </div>

            {/* Backup (JSON): Fortschritt und Ergebnis ausserhalb des Menues —
                das schliesst sich beim Klick, und das Laden dauert. */}
            {backupLauf ? (
                <div className="px-4 pb-3">
                    <div role="status" className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Backup wird erstellt — {backupLauf.geladen} von {backupLauf.gesamt} Projekten geladen …
                    </div>
                </div>
            ) : backupMeldung && (
                <div className="px-4 pb-3">
                    <div role="alert" className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${backupMeldung.art === 'fehler' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="flex-1">{backupMeldung.text}</span>
                        <button type="button" onClick={() => setBackupMeldung(null)} aria-label="Meldung schließen" title="Meldung schließen" className="w-6 h-6 -my-0.5 rounded-md inline-flex items-center justify-center shrink-0 opacity-70 hover:opacity-100">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5">
                {statusOptions.map(s => {
                    const aktiv = activeStatus === s;
                    return (
                        <button key={s} type="button" onClick={() => setStatusFilter(s)} aria-pressed={aktiv} className={`h-8 px-3 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap ${aktiv ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                            {s !== 'alle' && <span className={`w-1.5 h-1.5 rounded-full ${STATUS_PUNKT[s]}`} />}
                            {s === 'alle' ? 'Alle' : STATUS_LABEL[s]}
                            <span className={`text-[11px] tabular-nums ${aktiv ? 'text-slate-300' : 'text-slate-400'}`}>{statusCounts[s] || 0}</span>
                        </button>
                    );
                })}
                <span className="w-px h-5 bg-slate-200 mx-1.5" aria-hidden="true" />
                <button
                    type="button"
                    onClick={() => setShowOnlyNoInspections(!showOnlyNoInspections)}
                    aria-pressed={showOnlyNoInspections}
                    title="Projekte ohne durchgeführte Begehung (V0)"
                    className={`h-8 px-3 rounded-full text-[13px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap ${showOnlyNoInspections ? 'bg-rose-50 text-rose-700' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                    Ohne Begehung
                    <span className={`text-[11px] tabular-nums ${showOnlyNoInspections ? 'text-rose-500' : 'text-slate-400'}`}>{noInspectionCount}</span>
                    {showOnlyNoInspections && <X className="w-3.5 h-3.5" />}
                </button>
                <label className="relative inline-flex items-center">
                    <span className="sr-only">Verantwortlich</span>
                    <select
                        value={activeOwner}
                        onChange={(e) => setOwnerFilter(e.target.value)}
                        className={`h-8 pl-3 pr-7 rounded-full text-[13px] font-semibold appearance-none cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${activeOwner !== 'alle' ? 'bg-slate-800 text-white' : 'bg-transparent text-slate-600 hover:bg-slate-100'}`}
                    >
                        <option value="alle">Alle Verantwortlichen</option>
                        {ownerOptions.map(u => <option key={u.id} value={u.id}>{ownerName(u)}</option>)}
                    </select>
                    <ChevronDown className={`w-3.5 h-3.5 absolute right-2.5 pointer-events-none ${activeOwner !== 'alle' ? 'text-white' : 'text-slate-400'}`} />
                </label>
                <span className="ml-auto text-xs text-slate-500 tabular-nums">{sortedProjects.length} {sortedProjects.length === 1 ? 'Projekt' : 'Projekte'}</span>
            </div>

            <div className="overflow-x-auto border-t border-slate-100">
            <table className="w-full min-w-[880px] text-left">
                <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <tr>
                        <RenderSortHeader label="Projekt" sortKey="name" />
                        <RenderSortHeader label="Verantwortlich" sortKey="owner" />
                        <RenderSortHeader label="Status" sortKey="status" />
                        <RenderSortHeader label="Geändert" sortKey="updated_at" />
                        <th className="px-4 py-2.5"><span className="sr-only">Aktionen</span></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedProjects.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-12 text-center">
                            {searchTerm.trim() ? (
                                <>
                                    <div className="text-sm font-semibold text-slate-700">Nichts gefunden für „{searchTerm.trim()}“</div>
                                    <div className="text-xs text-slate-500 mt-1">Gesucht in Projektname, Nummer, Adresse, Verantwortlichen, Bauherr, Auftraggeber und Beteiligten.</div>
                                </>
                            ) : (
                                <div className="text-sm text-slate-500">Keine aktiven Projekte gefunden.</div>
                            )}
                        </td></tr>
                    ) : (pageProjects.map((p) => {
                            const meta = p.data.metadata as ReportMetadata;
                            const owner = users.find(u => u.id === p.user_id);
                            const status = listenStatus(p);
                            const nummer = angezeigteProjektnummer(p);
                            const adresse = angezeigteAdresse(p);
                            const fundstellen = searchResult.fundstellen.get(p.id) || [];
                            const inspNum = begehungsNummer(p);
                            // Die eigene Firma steht in jeder Zeile gleich — genannt
                            // wird sie nur, wo sie abweicht (Super-Admin).
                            const fremdeFirma = owner && currentUser && !sameCompany(owner, currentUser) ? owner.company_name : '';

                            const jsonId = meta.coordinatorId;
                            const jsonName = meta.coordinator;
                            const isMismatch = jsonId && jsonId !== p.user_id;
                            const isFixing = fixingOwnerId === p.id;

                            return (
                                <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
                                    <td className="px-4 py-3 min-w-0">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <button type="button" onClick={() => onOpenProject && onOpenProject(p)} className="text-sm font-semibold text-slate-800 hover:text-blue-700 text-left">
                                                <Markiert text={p.name || ''} begriffe={begriffe} />
                                            </button>
                                            <ProjectLockHint lock={projectLocks.get(p.id)} currentUserId={currentUser?.id} nowMs={lockNowMs} />
                                        </div>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                            <span className="font-mono text-slate-600">{nummer ? <Markiert text={nummer} begriffe={begriffe} /> : 'Keine Nr.'}</span>
                                            {adresse && (<>
                                                <span className="text-slate-300" aria-hidden="true">·</span>
                                                <span className="max-w-[28rem] truncate" title={adresse}><Markiert text={adresse} begriffe={begriffe} /></span>
                                            </>)}
                                            {inspNum === 0
                                                ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">V0 · keine Begehung</span>
                                                : <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 tabular-nums">V{inspNum}</span>}
                                        </div>
                                        {fundstellen.length > 0 && (
                                            <div className="mt-1.5 flex flex-col gap-0.5 max-w-[34rem]">
                                                {fundstellen.slice(0, 2).map((f, i) => (
                                                    <div key={i} className="text-xs text-slate-500 truncate" title={`${f.feld}: ${f.wert}`}>
                                                        <span className="text-slate-400">{f.feld}:</span> <Markiert text={f.wert} begriffe={begriffe} />
                                                    </div>
                                                ))}
                                                {fundstellen.length > 2 && <div className="text-[11px] text-slate-400">+{fundstellen.length - 2} weitere Treffer</div>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${isMismatch ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{owner ? initialen(owner.first_name, owner.last_name) : '?'}</span>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-sm text-slate-700 whitespace-nowrap"><Markiert text={ownerName(owner)} begriffe={begriffe} /></span>
                                                {fremdeFirma && <span className="text-xs text-slate-500 truncate">{fremdeFirma}</span>}
                                            </div>
                                        </div>
                                        {isMismatch && (
                                            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-red-700">
                                                <AlertTriangle className="w-3 h-3 shrink-0" />
                                                <span>Im Bericht: <span className="font-semibold">{jsonName}</span></span>
                                                {users.find(u => u.id === jsonId) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleQuickFixOwner(p.id, jsonId!)}
                                                        disabled={isFixing}
                                                        className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 hover:text-red-600 disabled:opacity-50"
                                                        title="DB Owner auf Berichts-Besitzer setzen"
                                                    >
                                                        {isFixing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5"/>}
                                                        angleichen
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 whitespace-nowrap">
                                            <span className={`w-2 h-2 rounded-full ${STATUS_PUNKT[status] || STATUS_PUNKT.planning}`} />
                                            {STATUS_LABEL[status] || status}
                                        </span>
                                        {kickoffKennzeichen.has(p.id) && (
                                            <div className="mt-1.5"><KickoffKennzeichenBadge kennzeichen={kickoffKennzeichen.get(p.id)} /></div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">{geaendertText(p.updated_at)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end items-center gap-0.5">
                                            <button type="button" onClick={() => onOpenProject && onOpenProject(p)} className="h-8 px-3 rounded-lg text-sm font-semibold text-blue-700 hover:bg-blue-50">Öffnen</button>
                                            <button type="button" onClick={() => setSupplementModal({ isOpen: true, project: p })} title="Nachtrag / Änderung" aria-label="Nachtrag / Änderung" className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"><FilePlus className="w-4 h-4"/></button>
                                            <button type="button" onClick={() => setTransferModal({ isOpen: true, projectId: p.id, projectName: p.name })} title="Übertragen" aria-label="Übertragen" className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"><ArrowRightLeft className="w-4 h-4"/></button>
                                            <button type="button" onClick={() => setDeleteActionProject(p)} title="Archivieren oder Löschung beantragen" aria-label="Archivieren oder Löschung beantragen" className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
            </div>

            <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                <label className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Pro Seite</span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="h-8 border border-slate-200 rounded-lg px-2 text-xs font-semibold text-slate-700 bg-white focus:ring-2 focus:ring-blue-300 outline-none"
                    >
                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n === 0 ? 'Alle' : n}</option>)}
                    </select>
                </label>

                {showPagination && (
                    <div className="flex items-center gap-3">
                        <span className="whitespace-nowrap tabular-nums">Zeige {pageStart + 1}–{pageEnd} von {sortedProjects.length}</span>
                        <Button variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} icon={<ChevronLeft className="w-4 h-4" />}>Zurück</Button>
                        <span className="whitespace-nowrap tabular-nums">Seite {safePage} von {totalPages}</span>
                        <Button variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>Weiter<ChevronRight className="w-4 h-4" /></Button>
                    </div>
                )}
            </div>

            {supplementModal.isOpen && <ProjectSupplementModal project={supplementModal.project} catalog={customCatalog} currentUser={currentUser} onSave={handleSaveSupplement} onClose={() => setSupplementModal({ isOpen: false, project: null })} />}
            
            <AdminCsvImportModal 
                isOpen={showCsvImportModal} 
                onClose={() => setShowCsvImportModal(false)} 
                onSuccess={() => { onRefresh(); setShowCsvImportModal(false); }} 
                currentUser={currentUser} 
            />

            {transferModal.isOpen && (
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                        <h3 className="font-bold text-lg mb-4">Projekt transferieren</h3>
                        <p className="text-sm text-slate-600 mb-4">Wählen Sie den neuen Besitzer für <strong>{transferModal.projectName}</strong>:</p>
                        <div className="mb-3"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="text" placeholder="Benutzer suchen..." value={transferSearch} onChange={(e) => setTransferSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"/></div></div>
                        <div className="max-h-48 overflow-y-auto mb-4 border border-slate-200 rounded-lg bg-slate-50">{transferUsers.map(u => (<div key={u.id} onClick={() => setTransferTargetId(u.id)} className={`p-3 text-sm cursor-pointer border-b last:border-0 hover:bg-blue-50 ${transferTargetId === u.id ? 'bg-blue-100 text-blue-700 font-bold' : 'text-slate-700'}`}>{u.first_name} {u.last_name} <span className="text-xs opacity-70">({u.email})</span></div>))}</div>
                        <div className="flex gap-3 justify-end"><Button variant="ghost" onClick={() => setTransferModal({ isOpen: false, projectId: null, projectName: '' })}>Abbrechen</Button><Button variant="primary" onClick={confirmTransfer} disabled={!transferTargetId}>Transferieren</Button></div>
                    </div>
                </div>
            )}

            {archivDialogProjekt && (
                <ArchivDokumenteDialog
                    projectId={archivDialogProjekt.id}
                    benutzerName={[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || currentUser?.email}
                    istAdmin
                    onAbbrechen={() => setArchivDialogProjekt(null)}
                    onAbschliessen={(entscheidung) => { void executeArchive(archivDialogProjekt, entscheidung); }}
                />
            )}

            {deleteActionProject && (
                <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                     <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                        <div className="flex items-start gap-4 mb-4"><div className="bg-red-50 p-3 rounded-full"><Trash2 className="w-6 h-6 text-red-600"/></div><div><h3 className="font-bold text-lg text-slate-800">Projekt löschen oder archivieren?</h3><p className="text-sm text-slate-600 mt-1">Sie sind dabei "{deleteActionProject.name}" zu entfernen.</p></div></div>
                        <div className="space-y-3">
                            <button onClick={() => { void startArchive(); }} disabled={isProcessingDelete} className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-orange-400 hover:bg-orange-50 transition-all group"><div className="flex items-center gap-2 font-bold text-slate-800 group-hover:text-orange-700"><Archive className="w-4 h-4"/> Archivieren (Empfohlen)</div><p className="text-xs text-slate-500 mt-1">Nur aus „Beendet" heraus und erst, wenn nichts mehr offen ist und die Schlussrechnung gestellt und bezahlt wurde. Das Projekt wird ausgeblendet.</p></button>
                            <button onClick={() => setLoeschAntragProjekt(deleteActionProject)} disabled={isProcessingDelete} className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-red-400 hover:bg-red-50 transition-all group"><div className="flex items-center gap-2 font-bold text-slate-800 group-hover:text-red-700"><Trash2 className="w-4 h-4"/> Löschung beantragen</div><p className="text-xs text-slate-500 mt-1">Vier-Augen-Prinzip: Sie begründen, ein zweiter Administrator gibt frei. Erst danach wird gelöscht.</p></button>
                        </div>
                        <div className="mt-4 flex justify-end"><Button variant="ghost" onClick={() => setDeleteActionProject(null)}>Abbrechen</Button></div>
                    </div>
                </div>
            )}

            {loeschAntragProjekt && (
                <ProjectDeleteRequestDialog
                    projektName={loeschAntragProjekt.name}
                    projektNummer={loeschAntragProjekt.project_number}
                    onBeantragen={beantrageLoeschung}
                    onAbbrechen={() => setLoeschAntragProjekt(null)}
                />
            )}
        </div>
    );
};
